/**
 * Pure-function state machine for attachment background uploads.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AttachmentBackgroundUploadStateMachine.swift`.
 *
 * ## Critical constraints
 *
 * ### Server-acked bytes ONLY
 * Progress is expressed exclusively via `ServerAckedProgressEvent.ackedBytes`,
 * which MUST come from the server's range-ack response. This module provides
 * no API surface that accepts client-buffered byte counts. The type system
 * enforces this: the only way to advance `serverAckedBytes` is through the
 * `serverAckedProgress` event, which callers must populate from the server
 * response, never from `XMLHttpRequest.upload.onprogress` or equivalent.
 *
 * ### Pure function
 * `transition` has no side effects, no global state, no DOM, no fetch().
 * The caller owns the timer, the network monitor, and the dispatch loop.
 *
 * ## State diagram
 *
 * ```
 * queued
 *   │ startUpload
 *   ▼
 * uploading ◀──────────────── networkRestored (from waitingNetwork)
 *   │   │   │   │
 *   │   │   │   └─ serverAckedProgress → uploading (progress update)
 *   │   │   └───── uploadComplete → completed (terminal)
 *   │   └───────── networkLost → waitingNetwork
 *   └─────────────── serverError
 *                       │
 *                       ├─ shouldRetry=true + cap not exhausted → retryBackoff
 *                       └─ shouldRetry=false | cap exhausted → failed (terminal)
 *
 * retryBackoff
 *   │ (tick: backoffUntilMs <= now)
 *   ▼
 * uploading (resume from serverAckedBytes)
 *
 * Any non-terminal + permanentError → failed (terminal)
 * ```
 */

import type {
  AttachmentBackgroundUploadState,
  AttachmentUploadEvent,
} from "./AttachmentBackgroundUpload";
import {
  shouldRetry,
  backoffDelayMs,
  isRetryCapExhausted,
} from "./AttachmentBackgroundUploadPolicy";

// ---------------------------------------------------------------------------
// transition — event-driven transitions
// ---------------------------------------------------------------------------

/**
 * Apply a single event to the current state and return the next state.
 *
 * Pure function: no side effects, no I/O, no clock access. `now` is only used
 * when computing the `backoffUntilMs` field of `retryBackoff` states; pass
 * `Date.now()` in production, or a fixed value in tests.
 *
 * @param state  - Current state.
 * @param event  - Event to apply.
 * @param now    - Current timestamp in milliseconds (used for backoff deadline).
 * @returns New state after applying the event.
 */
export function transition(
  state: AttachmentBackgroundUploadState,
  event: AttachmentUploadEvent,
  now: number,
): AttachmentBackgroundUploadState {
  // Terminal states absorb all events.
  if (state.kind === "completed" || state.kind === "failed") {
    return state;
  }

  // permanentError is handled the same way from any non-terminal state.
  if (event.kind === "permanentError") {
    return {
      kind: "failed",
      attachmentId: state.attachmentId,
      totalBytes: state.totalBytes,
      errorCode: event.errorCode,
      retryCount: "retryCount" in state ? state.retryCount : 0,
    };
  }

  switch (state.kind) {
    // ── queued ────────────────────────────────────────────────────────────────
    case "queued": {
      switch (event.kind) {
        case "startUpload":
          return {
            kind: "uploading",
            attachmentId: state.attachmentId,
            totalBytes: state.totalBytes,
            serverAckedBytes: 0,
            retryCount: 0,
          };
        // All other events in queued: no-op (upload hasn't started yet).
        default:
          return state;
      }
    }

    // ── uploading ─────────────────────────────────────────────────────────────
    case "uploading": {
      switch (event.kind) {
        case "serverAckedProgress":
          // CRITICAL: ackedBytes comes exclusively from the server's range-ack
          // response. The state machine has no other way to advance this field.
          return {
            ...state,
            serverAckedBytes: event.ackedBytes,
          };

        case "uploadComplete":
          return {
            kind: "completed",
            attachmentId: state.attachmentId,
            totalBytes: state.totalBytes,
          };

        case "networkLost":
          return {
            kind: "waitingNetwork",
            attachmentId: state.attachmentId,
            totalBytes: state.totalBytes,
            serverAckedBytes: state.serverAckedBytes,
            retryCount: state.retryCount,
          };

        case "serverError": {
          const nextRetryCount = state.retryCount + 1;
          // Fail immediately when the error is permanent or the cap is reached.
          // isRetryCapExhausted checks `nextRetryCount >= MAX_RETRY_COUNT` so
          // that the 6th retry attempt itself goes straight to `failed` rather
          // than queuing another backoff round (the cap is inclusive: retries
          // 1–5 back off and resume, the 6th error is terminal).
          if (!shouldRetry(event.errorCode) || isRetryCapExhausted(nextRetryCount)) {
            return {
              kind: "failed",
              attachmentId: state.attachmentId,
              totalBytes: state.totalBytes,
              errorCode: event.errorCode,
              retryCount: nextRetryCount,
            };
          }
          return {
            kind: "retryBackoff",
            attachmentId: state.attachmentId,
            totalBytes: state.totalBytes,
            serverAckedBytes: state.serverAckedBytes,
            retryCount: nextRetryCount,
            errorCode: event.errorCode,
            backoffUntilMs: now + backoffDelayMs(nextRetryCount),
          };
        }

        // startUpload while already uploading: idempotent no-op.
        default:
          return state;
      }
    }

    // ── waitingNetwork ────────────────────────────────────────────────────────
    case "waitingNetwork": {
      switch (event.kind) {
        case "networkRestored":
          // Resume from last server-acked offset.
          return {
            kind: "uploading",
            attachmentId: state.attachmentId,
            totalBytes: state.totalBytes,
            serverAckedBytes: state.serverAckedBytes,
            retryCount: state.retryCount,
          };

        case "networkLost":
          // Already waiting; no-op.
          return state;

        default:
          return state;
      }
    }

    // ── retryBackoff ──────────────────────────────────────────────────────────
    case "retryBackoff": {
      // Events in retryBackoff are generally no-ops until tick() fires.
      // However, networkLost is honoured to prevent a wasted retry on restore.
      switch (event.kind) {
        case "networkLost":
          return {
            kind: "waitingNetwork",
            attachmentId: state.attachmentId,
            totalBytes: state.totalBytes,
            serverAckedBytes: state.serverAckedBytes,
            retryCount: state.retryCount,
          };

        default:
          return state;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// tick — time-based backoff expiry
// ---------------------------------------------------------------------------

/**
 * Advance the backoff deadline. Must be called periodically by the owner
 * while in `retryBackoff` state (e.g. every 1 second).
 *
 * When the backoff deadline has passed, transitions `retryBackoff → uploading`
 * (resuming from the last server-acked offset).
 *
 * All other states are returned unchanged.
 *
 * @param state - Current state.
 * @param now   - Current timestamp in milliseconds.
 */
export function tick(
  state: AttachmentBackgroundUploadState,
  now: number,
): AttachmentBackgroundUploadState {
  if (state.kind !== "retryBackoff") {
    return state;
  }
  if (now >= state.backoffUntilMs) {
    return {
      kind: "uploading",
      attachmentId: state.attachmentId,
      totalBytes: state.totalBytes,
      serverAckedBytes: state.serverAckedBytes,
      retryCount: state.retryCount,
    };
  }
  return state;
}
